/**
 * Shipping rates — request/response contract for `POST /api/shipping/rates`.
 *
 * Single-seller per request (single-seller invariant — see
 * `.tondocs/payments/SINGLE_SELLER_INVARIANT_SPEC.md`). The endpoint quotes
 * live carrier rates for ONE seller's combined parcel to ONE destination.
 *
 * Rate at this stage is ESTIMATE only — the endpoint does NOT durably lock
 * a Shippo shipment. The buyer's selection is later persisted via the
 * checkout-intent `externalShipping` field; the actual label-buy re-rates
 * at the seller's Ready-to-ship click (PLAN §L).
 *
 * @see .tondocs/shippo/PLAN.md §D WS4 — workstream this belongs to
 * @see .tondocs/shippo/PLAN.md §L — rate is estimate, no durable lock here
 * @see .tondocs/shippo/PLAN.md §J.5 — single-seller invariant
 * @see .tondocs/skills/api-contract-design.md §A1 — error contract
 * @see lib/shipping/types.ts — `Rate` ADT this schema mirrors
 */

import { z } from "zod";
import { pinned } from "./pin";
import { isStateRequiredForCountry } from "./address-state";

// ---------------------------------------------------------------------------
// Address — buyer ship-to
// ---------------------------------------------------------------------------

/**
 * Ship-to address subset the rates endpoint needs. Country is the ISO-3166
 * alpha-2 code (`'US'` | `'CA'` for the MVP NA scope) — distinct from the
 * `CheckoutIntentAddress.country` (long-form name) because the rates
 * endpoint is upstream of the checkout-form long-form-to-ISO normaliser.
 *
 * Why a separate shape from `CheckoutIntentAddress`: the rates endpoint
 * fires DURING the address-entry sub-flow (§B step 3 re-sequencing) BEFORE
 * the buyer has fully filled the checkout form. Decoupling lets a partial
 * address (`name + zip + country`) rate without the full PayPal-ready set.
 */
export interface ShippingRatesAddress {
  /** Full name, used as Shippo address `name`. */
  name: string;
  /** Street line 1 — required by Shippo for address validation. */
  street1: string;
  /** Optional apartment / suite. */
  street2?: string;
  city: string;
  /** State / province (US: 2-letter; CA: 2-letter). Free text — Shippo normalises. */
  state: string;
  /** Postal code (US ZIP / CA postal). */
  zip: string;
  /**
   * ISO-3166 alpha-2. Acceptance is origin-tier driven (Option D,
   * 2026-06-20): the server rejects with `422 OUT_OF_ORIGIN` when the
   * SELLER's main-address country isn't in `SHIPPO_ORIGIN_SUPPORT`, and
   * with `400 NO_RATES_TO_DESTINATION` when a supported origin yields 0
   * carrier rates for this destination (sanctioned country / provider gap).
   * No silent fallback to legacy.
   */
  country: string;
  /**
   * Consignee phone (E.164 preferred, e.g. `"+33612345678"`). Optional at
   * the contract level — the checkout-form fills it whenever the buyer has
   * completed the phone fields (`phoneCountryCode + phoneNumber`).
   *
   * Threaded through to Shippo `addressTo.phone`. Two carriers refuse to
   * quote an intl shipment without a consignee phone: **DHL Express**
   * (Shippo returns a shipment-level message
   * `"DHL Express requires a phone number for the consignee address."`),
   * and USPS Priority Mail Intl requires it for customs clearance to most
   * destinations. Empty/absent → DHL is silently dropped from `rates[]`
   * (repro'd 2026-07-03 US→FR: 4 rates → 3 rates → 2 rates as phone/floor
   * gaps compound).
   *
   * @see HANDOFF_BASERATE_FR_2026_06_30.md §4 Hyp A — DHL/USPS phone requirement
   */
  phone?: string;
  /**
   * Consignee email. Optional. Threaded through to Shippo `addressTo.email`
   * — some carriers surface tracking / customs correspondence over it, and
   * DHL Express intl paperwork includes it. Not the deciding factor for
   * quote availability (unlike `phone`) but sent when we have it because
   * it costs nothing and unblocks a future carrier that might require it.
   */
  email?: string;
}

// ---------------------------------------------------------------------------
// Parcel — physical dimensions of the combined seller parcel
// ---------------------------------------------------------------------------

/**
 * Parcel dimensions for the rates request. Mirrors `ParcelDims` in
 * `lib/shipping/types.ts` shape-for-shape — kept inline here because the
 * `@kraahl/contract` package must not import from `lib/` (would create a
 * cycle: app → contract → lib → contract).
 *
 * Units are literal-union strings (NOT a `Unit` object) per the same
 * left-fix discipline as `ParcelDims`.
 */
export interface ShippingRatesParcel {
  weight: number;
  length: number;
  width: number;
  height: number;
  /**
   * Post-K.6 (PLAN §A "Parcel units" policy C, 2026-06-22) the canonical
   * inputs are `"g"` + `"mm"`. The legacy `lb`/`oz`/`kg`/`in`/`cm` variants
   * remain on the wire for slice C1's transition (UI form refactor = C2);
   * the server-side rate builder (`lib/shipping/rates.ts`) normalises to
   * `"g"` + `"mm"` before calling Shippo.
   */
  massUnit: "lb" | "oz" | "kg" | "g";
  distanceUnit: "in" | "cm" | "mm";
}

// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------

/**
 * Request body for `POST /api/shipping/rates`.
 *
 * Single-seller invariant: exactly ONE `sellerId`. Multi-seller payloads are
 * rejected with 400 `MULTI_SELLER_FORBIDDEN` — the contract carries no
 * `sellerIds[]` array by design (Make Illegal States Unrepresentable).
 *
 * Provider-neutral: the route does NOT take a `provider` field. The server
 * resolves the seller's configured provider (currently `'shippo'`-only)
 * from `shipping_accounts`. Adding a second provider later is a server-side
 * dispatch change, not a wire-shape change.
 */
export interface ShippingRatesRequest {
  /** `profiles.id` of the seller this parcel ships from. */
  sellerId: string;
  /**
   * Optional `listings.id` for the item being rated. When present, the
   * server resolves the ship-from address via the listing's
   * `ship_from_address_id` pin per ADR_LISTING_SHIP_FROM_ADDRESS.md;
   * `null`/absent falls back to the seller's `address_book.is_main` row
   * (preserves legacy behaviour). Single-seller invariant — single
   * listing in MVP cart use; multi-item cart consumers pass the listing
   * id that drives the parcel + carrier choice.
   *
   * Additive optional under Phase 1 — pre-existing callers (and mobile)
   * that do not send this field continue to receive `is_main`-resolved
   * rates byte-for-byte.
   *
   * @see .tondocs/listings/ADR_LISTING_SHIP_FROM_ADDRESS.md
   */
  listingId?: string;
  /** Destination address. NA-only. */
  address: ShippingRatesAddress;
  /** Combined parcel for the seller's items (summed weight + max-per-axis DIM). */
  parcel: ShippingRatesParcel;
}

// ---------------------------------------------------------------------------
// Response — success
// ---------------------------------------------------------------------------

/**
 * One quote returned to the buyer. Provider-neutral: no `provider` field
 * (carrier + service identify the option for the buyer; the provider that
 * sourced the rate is an internal detail).
 *
 * `id` IS the carrier's rate object_id (Shippo `object_id`). The buyer
 * round-trips it through `CheckoutIntent.externalShipping.rateId`, and the
 * checkout resolver hands it straight back to `shippo.rates.get()` — they
 * MUST be bytewise-equal. The opaque-token indirection that briefly lived
 * here (2026-06-18 → 2026-06-25) was reverted; see
 * [[project_opaque_rate_id_revert_2026_06_25]]. Provider-neutrality is
 * preserved by NAMING (`carrier`, `service`, no `provider` field) and
 * parse-on-boundary, not by a synthetic id.
 */
export interface ShippingRatesQuote {
  id: string;
  carrier: string;
  service: string;
  /** Buffered amount (§I.1). Major units. */
  amount: number;
  /** ISO 4217 (`'USD' | 'CAD'`). NA-only. */
  currency: string;
  /** Carrier-estimated transit days, when provided. */
  durationDays: number | null;
  /**
   * Provider shipment id — round-trips back through
   * `CheckoutIntent.externalShipping.shipmentId` and lands in
   * `seller_orders.external_shipment_id` for the seller's later label buy.
   * One id per quote round, identical across every `Rate` in the response.
   */
  shipmentId: string;
}

export interface ShippingRatesResponse {
  rates: ShippingRatesQuote[];
  /**
   * Seller's ship-from country, ISO-3166 alpha-2 (UPPER-CASE). Server-side
   * SoT for "is this an international shipment" — derived from the
   * resolved `from_address` (per-listing pin or `address_book.is_main`
   * fallback). The buyer-side `LiveRateSubflow` compares
   * `fromCountry !== address.country.toUpperCase()` to surface the
   * "International rate may adjust at label purchase" disclosure.
   *
   * Why this lives on the response (and not on the rate-card or on a
   * separate "seller-profile" lookup): the SAME server boundary that
   * picked `addressFrom` for Shippo already owns the truth. Surfacing it
   * back avoids a parallel-channel lookup (Sibling-F anchor 2) — the
   * checkout-side disclosure is rendered from THIS field, the
   * ready-to-ship label-buy re-rates from the persisted
   * `seller_orders.from_address` snapshot (PR-G F6 / `8c4c7889`). Same
   * SoT, two surfaces. See `.tondocs/shippo/HANDOFF_RELIABILITY_GAPS_2026_06_27.md`
   * §"PR-G шаг 3" — estimate-vs-truth policy.
   */
  fromCountry: string;
}

// ---------------------------------------------------------------------------
// Zod schemas — pinned to the interfaces above
// ---------------------------------------------------------------------------

// `state` is `z.string()` (not `min(1)`) and gated by a `superRefine` against
// `isStateRequiredForCountry(country)` because PayPal/Shippo only treat state
// as required for US/CA. Buyers in FR/UK/JP/… legitimately submit `state: ""`.
// Pre-2026-06-25 this was `z.string().min(1)` and silently rejected every
// non-US/CA buyer's live-rate request — caller (`buildShippingRatesRequest`)
// then surfaced nothing useful. See [[project_state_validation_desync_us_ca_only]].
export const ShippingRatesAddressSchema = pinned<ShippingRatesAddress>()(
  z
    .object({
      name: z.string().min(1),
      street1: z.string().min(1),
      street2: z.string().optional(),
      city: z.string().min(1),
      state: z.string(),
      zip: z.string().min(1),
      country: z.string().min(2),
      phone: z.string().optional(),
      email: z.string().optional(),
    })
    .refine(
      (a) => !isStateRequiredForCountry(a.country) || a.state.trim().length > 0,
      { path: ["state"], message: "state required for US/CA" },
    ),
);

export const ShippingRatesParcelSchema = pinned<ShippingRatesParcel>()(
  z.object({
    weight: z.number().positive(),
    length: z.number().positive(),
    width: z.number().positive(),
    height: z.number().positive(),
    massUnit: z.enum(["lb", "oz", "kg", "g"]),
    distanceUnit: z.enum(["in", "cm", "mm"]),
  }),
);

export const ShippingRatesRequestSchema = pinned<ShippingRatesRequest>()(
  z.object({
    sellerId: z.string().min(1),
    listingId: z.string().uuid().optional(),
    address: ShippingRatesAddressSchema,
    parcel: ShippingRatesParcelSchema,
  }),
);

export const ShippingRatesQuoteSchema = pinned<ShippingRatesQuote>()(
  z.object({
    id: z.string().min(1),
    carrier: z.string().min(1),
    service: z.string().min(1),
    amount: z.number().nonnegative(),
    currency: z.string().min(3),
    durationDays: z.number().int().nullable(),
    shipmentId: z.string().min(1),
  }),
);

export const ShippingRatesResponseSchema = pinned<ShippingRatesResponse>()(
  z.object({
    rates: z.array(ShippingRatesQuoteSchema),
    fromCountry: z.string().min(2),
  }),
);
